/**
 * UserPointHistoryPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  ModalLayout,
  UserList,
  Text,
} from 'components';
import {
  Avatar,
  Button,
  DatePicker,
  Radio,
  Table,
  Spin,
  Tag,
  Flex,
  InputNumber,
  Input,
} from 'antd';
import { Link } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';
import { strCut, stringToMoneyFormat } from 'utils';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';
import { POINT_TYPE } from 'constants/PointContants';
const INITIAL_VALUE = {
  type: '',
  point_type: '',
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
};
const SUMMARY_VALUE = {
  all_users_point: 0,
  total_point: 0,
  not_expired_point: 0,
  used_point: 0,
  expired_point: 0,
  expired_7ago_point: 0,
};

const EXPIRY = 7;
const EXPIRY_DATE = dayjs().startOf('day').unix();
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 포인트내역
 * ----------------------------------------
 */
const UserPointHistoryPresenter = ({
  userList,
  permission,
  loading,
  currentUser,
  setCurrentUser,
  setPeriod,
  PointHistoryList,
  PointHistoryLogList,
  onChange,
  inputValue,
  setInputValue,
  setLoading,
  onReservePoint,
  // onSelect,
  // selectedList,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [filterItem, setFilterItem] = React.useState(INITIAL_VALUE);
  const [filterList, setFilterList] = React.useState(userList);
  const [summary, setSummary] = React.useState(SUMMARY_VALUE);
  const [currentUserData, setCurrentUserData] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [tabType, setTabType] = React.useState('POINT');
  const [buttonType, setButtonType] = React.useState(null);
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '적립 유형',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.type}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, type: e.target.value }))
              }
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'ADMIN'}>관리자</Radio>
              <Radio value={'SHOP'}>쇼핑몰</Radio>
              <Radio value={'ORDER'}>주문</Radio>
              <Radio value={'REVIEW'}>리뷰</Radio>
              <Radio value={'RENTAL'}>렌탈</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '적립 타입',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.point_type}
              onChange={(e) =>
                setFilterItem((prev) => ({
                  ...prev,
                  point_type: e.target.value,
                }))
              }
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'EARNED'}>적립</Radio>
              <Radio value={'REFUNDED'}>환불적립</Radio>
              <Radio value={'EXPIRED'}>만료</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '적립일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={filterItem.value}
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    value: e.target.value,
                    start_date: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end_date: dayjs().endOf('day'),
                  }))
                }
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(filterItem.start_date)}
                  onChange={(e) =>
                    setFilterItem((prev) => ({
                      ...prev,
                      start_date: dayjs(e).startOf('day'),
                    }))
                  }
                  maxDate={filterItem.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dayjs(filterItem.end_date)}
                  onChange={(e) =>
                    setFilterItem((prev) => ({
                      ...prev,
                      end_date: dayjs(e).endOf('day'),
                    }))
                  }
                  minDate={filterItem.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  const pointCol = [
    {
      title: '포인트번호',
      dataIndex: 'point_history_id',
      key: 'point_history_id',
      width: 100,
      sorter: {
        compare: (a, b) => a.point_history_id - b.point_history_id,
        multiple: 2,
      },
      render: (point_history_id) => <>{point_history_id}</>,
    },
    {
      title: '이름(아이디)',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: () => (
        <>
          <Avatar style={{ marginRight: 5 }}>
            {currentUserData.user_name}
          </Avatar>
          <Link
            to={`/users/list?modal=${encodeURIComponent(
              encrypt(currentUserData.user_id)
            )}`}
          >
            {strCut(currentUserData.user_name, 10)}(
            {strCut(currentUserData.user_account, 10)})
          </Link>
        </>
      ),
    },
    {
      title: '매장명',
      dataIndex: 'shop_information_name',
      key: 'shop_information_name',
      width: 150,
      render: (shop_information_name) => <>{shop_information_name}</>,
    },
    {
      title: '적립 타입',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      filters: [
        { text: '적립', value: 'EARNED' },
        { text: '환불적립', value: 'REFUNDED' },
        { text: '만료', value: 'EXPIRED' },
      ],
      onFilter: (value, record) => record.type.includes(value),
      render: (type, _item) => {
        return (
          <>
            <Tag {...{ color: POINT_TYPE[type]?.color }}>
              {POINT_TYPE[type]?.label}
            </Tag>
          </>
        );
      },
    },
    {
      title: '적립 유형(관련 번호)',
      dataIndex: 'earned_type',
      key: 'earned_type',
      width: 150,
      render: (_, _item) => {
        const orderType = _item?.order_id
          ? { color: 'blue', label: '주문', id: _item?.order_id }
          : _item?.parcel_service_id
          ? {
              color: 'purple',
              label: '쇼핑몰',
              id: _item?.parcel_service_id,
            }
          : _item?.rental_id
          ? { color: 'green', label: '렌탈', id: _item?.rental_id }
          : _item?.review_id
          ? { color: 'orange', label: '리뷰', id: _item?.review_id }
          : { color: 'default', label: '관리자', id: null };

        return (
          <>
            <Tag color={orderType.color}>{orderType.label}</Tag>
            {orderType.id && `(${orderType.id})`}
          </>
        );
      },
    },
    {
      title: '적립 포인트',
      dataIndex: 'point',
      key: 'point',
      width: 100,
      sorter: {
        compare: (a, b) => a?.point - b?.point,
        multiple: 2,
      },
      render: (point) => <>{point} P</>,
    },
    {
      title: '미사용 포인트',
      dataIndex: 'not_use_point',
      key: 'not_use_point',
      width: 120,
      sorter: {
        compare: (a, b) => {
          const nowDateUnix = dayjs().unix();
          const aPoint =
            nowDateUnix > a?.expired_date ? 0 : a?.point - a?.use_point;
          const bPoint =
            nowDateUnix > b?.expired_date ? 0 : b?.point - b?.use_point;
          return aPoint - bPoint;
        },
        multiple: 2,
      },
      render: (_, _item) => (
        <>
          {dayjs().unix() > _item?.expired_date
            ? 0
            : _item?.point - _item?.use_point}{' '}
          P
        </>
      ),
    },
    {
      title: '사용 포인트',
      dataIndex: 'use_point',
      key: 'use_point',
      width: 100,
      sorter: {
        compare: (a, b) => a.use_point - b.use_point,
        multiple: 2,
      },
      render: (use_point) => <>{use_point} P</>,
    },
    {
      title: '만료 포인트',
      dataIndex: 'expired_point',
      key: 'expired_point',
      width: 100,
      render: (_, _item) => (
        <>
          {dayjs().unix() > _item?.expired_date
            ? _item?.point - _item?.use_point
            : 0}{' '}
          P
        </>
      ),
    },
    {
      title: '적립일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 155,
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
      render: (created_at) =>
        dayjs(created_at * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
    {
      title: '만료 예정일',
      dataIndex: 'expired_date',
      key: 'expired_date',
      width: 155,
      sorter: {
        compare: (a, b) => a.expired_date - b.expired_date,
        multiple: 2,
      },
      render: (expired_date) =>
        dayjs(expired_date * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
  ];

  const pointLogCol = [
    {
      title: '로그번호',
      dataIndex: 'point_history_log_id',
      key: 'point_history_log_id',
      width: 100,
      sorter: {
        compare: (a, b) => a.point_history_log_id - b.point_history_log_id,
        multiple: 2,
      },
      render: (point_history_log_id) => <>{point_history_log_id}</>,
    },
    {
      title: '포인트번호',
      dataIndex: 'point_history_id',
      key: 'point_history_id',
      width: 150,
      sorter: {
        compare: (a, b) => a.point_history_id - b.point_history_id,
        multiple: 2,
      },
      render: (point_history_id) => <>{point_history_id}</>,
    },
    {
      title: '이름(아이디)',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: () => (
        <>
          <Avatar style={{ marginRight: 5 }}>
            {currentUserData.user_name}
          </Avatar>
          <Link
            to={`/users/list?modal=${encodeURIComponent(
              encrypt(currentUserData.user_id)
            )}`}
          >
            {strCut(currentUserData.user_name, 10)}(
            {strCut(currentUserData.user_account, 10)})
          </Link>
        </>
      ),
    },
    {
      title: '매장명',
      dataIndex: 'shop_information_name',
      key: 'shop_information_name',
      width: 150,
      render: (shop_information_name) => <>{shop_information_name}</>,
    },
    {
      title: '적립 타입',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      filters: Object.entries(POINT_TYPE).map(([key, v]) => ({
        text: v.label,
        value: key,
      })),
      onFilter: (value, record) => record.type.includes(value),
      render: (type, _item) => {
        return (
          <>
            <Tag {...{ color: POINT_TYPE[type]?.color }}>
              {POINT_TYPE[type]?.label}
            </Tag>
          </>
        );
      },
    },
    {
      title: '적립 유형(관련 번호)',
      dataIndex: 'earned_type',
      key: 'earned_type',
      width: 150,
      render: (_, _item) => {
        const orderType = _item?.order_id
          ? { color: 'blue', label: '주문', id: _item?.order_id }
          : _item?.parcel_service_id
          ? {
              color: 'purple',
              label: '쇼핑몰',
              id: _item?.parcel_service_id,
            }
          : _item?.rental_id
          ? { color: 'green', label: '렌탈', id: _item?.rental_id }
          : _item?.review_id
          ? { color: 'orange', label: '리뷰', id: _item?.review_id }
          : { color: 'default', label: '관리자', id: null };

        return (
          <>
            <Tag color={orderType.color}>{orderType.label}</Tag>
            {orderType.id && `(${orderType.id})`}
          </>
        );
      },
    },
    {
      title: '포인트',
      dataIndex: 'point',
      key: 'point',
      width: '7%',
      sorter: {
        compare: (a, b) => a.point - b.point,
        multiple: 2,
      },
      render: (point) => <>{point}P</>,
    },
    {
      title: '매모/사유',
      dataIndex: 'memo',
      key: 'memo',
      width: '20%',
      render: (memo) => <>{memo}</>,
    },
    {
      title: '생성일자',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 155,
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
      render: (created_at) =>
        dayjs(created_at * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
  ];
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   * 포인트 요약 계산
   * --
   * @param {*} type
   * @returns
   */
  const calculatePoint = (type) => {
    const nowDateUnix = dayjs().unix();
    const user = userList.find((user) => user.user_id === currentUser);
    switch (type) {
      case 'ALL_USERS_POINT':
        return userList?.reduce((acc, user) => {
          const userPoint = user?.point_history?.reduce((acc, point) => {
            return (acc += point?.point);
          }, 0);
          return acc + userPoint;
        }, 0);

      case 'ALL_POINT':
        return user?.point_history?.reduce((acc, point) => {
          return (acc += point?.point);
        }, 0);

      case 'NOT_EXPIRED_POINT':
        return user?.point_history?.reduce((acc, point) => {
          const remainingPoint = point?.point - point?.use_point;
          if (nowDateUnix < point?.expired_date) {
            acc += remainingPoint; // 유효한 포인트 합산
          }
          return acc;
        }, 0);

      case '7AGO_EXPIRED_POINT':
        return user?.point_history?.reduce((acc, point) => {
          const remainingPoint = point?.point - point?.use_point;
          const expDay = dayjs
            .unix(point?.expired_date)
            .diff(dayjs.unix(EXPIRY_DATE), 'day');
          if (
            point?.type === 'EARNED' &&
            nowDateUnix < point?.expired_date &&
            expDay >= 0 &&
            expDay <= EXPIRY
          ) {
            acc += remainingPoint; // 유효한 포인트 합산
          }
          return acc;
        }, 0);

      case 'USED_POINT':
        return user?.point_history?.reduce((acc, point) => {
          return (acc += point?.use_point);
        }, 0);

      case 'EXPIRED_POINT':
        return user?.point_history?.reduce((acc, point) => {
          const isExpired = nowDateUnix > point?.expired_date;
          return (acc += isExpired ? point.point - point.use_point : 0);
        }, 0);

      case 'REFUNDED_POINT':

      default:
        return 0;
    }
  };
  /* ===== FUNCTION ===== */
  /**
   * 포인트 조건 검색
   * --
   * @param {string} t
   */
  const handleSearch = (t) => {
    setLoading(true);
    const searchType = t.toLocaleUpperCase();
    let arr = userList;
    switch (searchType) {
      case 'SEARCH':
        const { type, point_type } = filterItem;
        if (type) {
          arr = arr.map((user) => ({
            ...user,
            point_history: user?.point_history?.filter((point) => {
              if (type === '') {
                return true;
              }
              //
              if (type === 'SHOP') {
                return point?.parcel_service_id || point.shop_order_id
                  ? true
                  : false;
              }
              //
              if (type === 'ORDER') {
                return point?.order_id ? true : false;
              }
              //
              if (type === 'RENTAL') {
                return point.rental_id ? true : false;
              }
              //
              if (type === 'REVIEW') {
                return point.review_id ? true : false;
              }
              //
              if (type === 'ADMIN') {
                return !point?.parcel_service_id &&
                  !point.shop_order_id &&
                  !point?.order_id &&
                  !point.rental_id &&
                  !point.review_id
                  ? true
                  : false;
              }
            }),
          }));
        }
        if (point_type) {
          arr = arr.map((user) => ({
            ...user,
            point_history: user?.point_history?.filter((point) => {
              if (point_type === '') {
                return true;
              }
              if (point_type === 'EARNED') {
                return point.type === 'EARNED';
              }
              if (point_type === 'REFUNDED') {
                return point.type === 'REFUNDED';
              }
              if (point_type === 'EXPIRED') {
                return point.type === 'EXPIRED';
              }
            }),
          }));
        }
        break;
      default:
        break;
    }
    arr = arr?.filter((user) => user?.point_history?.length > 0);

    const sort = arr.map((user) => ({
      ...user,
      point_history: user?.point_history.sort(
        (a, b) => b.created_at - a.created_at
      ),
    }));
    setSummary((prev) => ({
      ...prev,
      all_users_point: sort?.reduce((acc, user) => {
        const userPoint = user?.point_history?.reduce((acc, point) => {
          return (acc += point?.point);
        }, 0);
        return acc + userPoint;
      }, 0),
    }));
    setFilterList(sort);
    setCurrentUser(null);
    setCurrentUserData(null);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };
  /**
   * 엑셀 다운로드
   * ---
   * @returns
   */
  const handleXlsx = () => {
    let columns = [];
    let rows = [];
    const earnedType = {
      ORDER: '주문',
      SHOP: '쇼핑몰',
      RENTAL: '렌탈',
      REVIEW: '리뷰',
      ADMIN: '관리자',
    };
    // columns, rows,
    if (tabType === 'POINT') {
      columns = pointCol;

      columns.splice(1, 1, {
        title: '이름',
        dataIndex: 'user_name',
        key: 'user_name',
      });
      columns.splice(2, 0, {
        title: '아이디',
        dataIndex: 'user_account',
        key: 'user_account',
      });
      columns.splice(3, 0, {
        title: '휴대폰번호',
        dataIndex: 'user_phone',
        key: 'user_phone',
      });
      rows = currentUserData?.point_history?.map((point) => {
        return {
          ...point,
          type: POINT_TYPE[point?.type]?.label,
          earned_type: point?.order_id
            ? earnedType['ORDER']
            : point?.parcel_service_id
            ? earnedType['SHOP']
            : point?.rental_id
            ? earnedType['RENTAL']
            : point?.review_id
            ? earnedType['REVIEW']
            : earnedType['ADMIN'],
          not_use_point:
            dayjs().unix() > point?.expired_date
              ? 0
              : point?.point - point?.use_point,
          expired_point:
            dayjs().unix() > point?.expired_date
              ? point?.point - point?.use_point
              : 0,
          ///
          shop_information_name: point?.shop_information_name,
          user_name: currentUserData.user_name,
          user_account: currentUserData.user_account,
          user_phone: currentUserData.user_phone,
        };
      });
      return {
        columns: columns,
        rows: rows,
        file_name: '포인트적립내역',
      };
    }
    if (tabType === 'LOG') {
      columns = pointLogCol;

      columns.splice(2, 1, {
        title: '이름',
        dataIndex: 'user_name',
        key: 'user_name',
      });
      columns.splice(3, 0, {
        title: '아이디',
        dataIndex: 'user_account',
        key: 'user_account',
      });
      columns.splice(4, 0, {
        title: '휴대폰번호',
        dataIndex: 'user_phone',
        key: 'user_phone',
      });
      rows = currentUserData?.point_history
        ?.map((point) => {
          return point?.point_history_log?.map((log) => ({
            ...log,
            earned_type: log?.order_id
              ? earnedType['ORDER']
              : log?.parcel_service_id || log?.shop_order_id
              ? earnedType['SHOP']
              : log?.rental_id
              ? earnedType['RENTAL']
              : log?.review_id
              ? earnedType['REVIEW']
              : earnedType['ADMIN'],
            type: POINT_TYPE[log?.type]?.label,
            ///
            shop_information_name: point?.shop_information_name,
            user_name: currentUserData?.user_name,
            user_account: currentUserData?.user_account,
            user_phone: currentUserData?.user_phone,
          }));
        })
        .flat();
      return {
        columns: columns,
        rows: rows,
        file_name: '포인트적립내역상세',
      };
    }
  };

  /* ===== Hooks ===== */
  /**
   * 회원 선택
   * ---
   */
  React.useEffect(() => {
    const filterUser = filterList?.find(
      (user) => user?.user_id === currentUser
    );

    const newData = filterUser
      ? {
          ...filterUser,
          point_history: [...filterUser?.point_history]?.sort(
            (a, b) => b?.created_at - a?.created_at
          ),
        }
      : null;
    setCurrentUserData(newData);
  }, [currentUser, filterList]);
  /**
   * 요약
   * ---
   */
  React.useEffect(() => {
    setSummary((prev) => ({
      ...prev,
      total_point: stringToMoneyFormat(calculatePoint('ALL_POINT')),
      not_expired_point: stringToMoneyFormat(
        calculatePoint('NOT_EXPIRED_POINT')
      ),
      used_point: stringToMoneyFormat(calculatePoint('USED_POINT')),
      expired_point: stringToMoneyFormat(calculatePoint('EXPIRED_POINT')),
      expired_7ago_point: stringToMoneyFormat(
        calculatePoint('7AGO_EXPIRED_POINT')
      ),
    }));
  }, [currentUser, PointHistoryList, PointHistoryLogList]);

  /**
   * 검색
   * --
   */
  React.useEffect(() => {
    handleSearch('search');
  }, [PointHistoryList, PointHistoryLogList]);

  return (
    <>
      {/* === Loading - start === */}
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        {/* ===== 헤더&필터 - start ===== */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 타이틀 === */}
          <Title style={{ ...styles.title }}>포인트 사용 내역</Title>

          {/* === 필터 - start === */}
          <ExpandableContainer borderRadius={4} padding={20}>
            {formItem.map((_item, _index) => (
              <div
                key={`form_${_index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 0px',
                }}
              >
                <div style={{ width: 120, fontSize: 13, fontWeight: 400 }}>
                  <label>{_item.label}</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {_item.form.map((_subitem, _subkey) => (
                    <React.Fragment key={`input_${_subkey}`}>
                      {_subitem.input}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableContainer>

          {/* 검색/초기화 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 13,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{
                ...styles.button,
                background: 'var(--primary-color)',
              }}
              onClick={() => {
                setPeriod({
                  start_date: filterItem.start_date.unix(),
                  end_date: filterItem.end_date.unix(),
                });
                handleSearch('search');
              }}
            >
              검색
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => {
                setPeriod({
                  start_date: INITIAL_VALUE.start_date.unix(),
                  end_date: INITIAL_VALUE.end_date.unix(),
                });
                setFilterItem(INITIAL_VALUE);
              }}
            >
              초기화
            </Button>
          </div>
          {/* 검색/초기화 버튼 끝 */}
          {/* === 필터 - end === */}
        </Content>
        {/* ===== 헤더&필터 - end ===== */}

        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 통계 - start === */}
          <Content
            maxWidth={'100%'}
            padding={0}
            backgroundColor={'none'}
            style={{
              marginBottom: 4,
              display: 'flex',
              gap: '2px',
            }}
          >
            {[
              {
                title: '기간별 회원 총 적립 포인트',
                value: summary.all_users_point,
                suffix: 'P',
              },
              {
                title: '회원 총 적립 포인트',
                value: summary.total_point,
                suffix: 'P',
              },
              {
                title: '미사용 포인트',
                value: summary.not_expired_point,
                suffix: 'P',
              },
              {
                title: '사용 포인트',
                value: summary.used_point,
                suffix: 'P',
              },
              {
                title: '만료 예정 포인트(7일 기준)',
                value: summary.expired_7ago_point,
                suffix: 'P',
              },
              {
                title: '만료 포인트',
                value: summary.expired_point,
                suffix: 'P',
              },
            ].map((_item, _index) => (
              <div
                key={`${_item.label}-${_index}`}
                className="boxShadow"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 4,
                  width: '100%',
                  margin: '2px',
                  padding: '26px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Statistic
                  style={{ justifyContent: 'center' }}
                  title={_item.title}
                  value={_item.value}
                  suffix={_item.suffix}
                  direction={'row'}
                />
              </div>
            ))}
          </Content>
          {/* === 통계 - end === */}

          {/* ===== 하단컨텐츠 - start ===== */}
          <Row gutter={[1, 1]} className="boxShadow">
            {/* === 회원목록 - start === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <UserList
                  users={filterList}
                  selectedUser={currentUser}
                  fetch={false}
                  onChange={setCurrentUser}
                  // query={{ filter: 'user_name' }}
                  maxHeight={611}
                />
              </Col>
            )}
            {/* === 회원목록 - end === */}

            {/* === 리뷰목록 - start === */}
            <Col x={permission === 'ADMIN' ? 20 : 24}>
              <Content
                maxWidth={'100%'}
                width={1400}
                height={'100%'}
                minHeight={490}
                maxHeight={662}
                borderRadius={4}
                padding={20}
              >
                {/* === 헤더 === */}
                <Flex
                  vertical
                  gap="middle"
                  style={{ width: '400px', marginBottom: '10px' }}
                >
                  <Radio.Group
                    block
                    options={[
                      {
                        label: '적립 내역',
                        value: 'POINT',
                      },
                      {
                        label: '적립 내역 상세',
                        value: 'LOG',
                      },
                    ]}
                    defaultValue={tabType}
                    optionType="button"
                    buttonStyle="solid"
                    onChange={(e) => setTabType(e.target.value)}
                  />
                </Flex>
                <div>
                  <Title
                    size={4}
                    style={{ marginBottom: 10 }}
                    subButton={
                      <Flex gap={4}>
                        <Button
                          onClick={() => {
                            const { columns, rows, file_name } = handleXlsx();
                            downloadXlsx({
                              columns: columns,
                              rows: rows,
                              fileName: file_name,
                            });
                          }}
                        >
                          {tabType === 'POINT' ? '적립 내역' : '적립 내역 상세'}{' '}
                          엑셀로 다운로드
                        </Button>
                        <Button
                          disabled={!currentUser}
                          onClick={() => {
                            setIsOpen(!isOpen);
                            setButtonType('REWARD');
                          }}
                        >
                          적립금 지급
                        </Button>
                        <Button
                          disabled={
                            !currentUser ||
                            (summary.not_expired_point === 0 && true)
                          }
                          onClick={() => {
                            setIsOpen(!isOpen);
                            setButtonType('DEDUCTED');
                          }}
                        >
                          적립금 차감
                        </Button>
                      </Flex>
                    }
                  >
                    <span>
                      검색결과 총{' '}
                      <a>{currentUserData?.point_history?.length || 0}</a>건
                    </span>
                  </Title>
                </div>

                {/* === 포인트 목록 === */}
                {tabType === 'POINT' ? (
                  <Table
                    size="small"
                    scroll={{
                      x: 1250,
                      y: 49 * 10,
                    }}
                    dataSource={currentUserData?.point_history}
                    columns={pointCol}
                  />
                ) : (
                  <Table
                    size="small"
                    scroll={{
                      x: 1250,
                      y: 49 * 10,
                    }}
                    dataSource={currentUserData?.point_history
                      .map((point) => point.point_history_log)
                      .flat()
                      ?.sort((a, b) => b?.created_at - a?.created_at)}
                    columns={pointLogCol}
                  />
                )}
              </Content>
            </Col>
          </Row>
          {/* ===== 하단컨텐츠 - end ===== */}
        </Content>
      </Spin>
      {/* === Loading - start === */}

      <ModalLayout
        open={isOpen}
        onCancel={() => {
          setInputValue({
            point: 0,
            expired_date: 0,
          });
          setIsOpen(!isOpen);
        }}
        width={300}
        centered
      >
        <Content maxWidth={'100%'} padding={0}>
          <Flex>
            <Text style={{ marginBottom: '10px' }}>
              {buttonType === 'REWARD'
                ? '적립금을 지급합니다.'
                : '적립금을 적립된 순서대로 차감합니다.'}
            </Text>
          </Flex>
          {buttonType === 'REWARD' ? (
            <Flex alignItems="center" gap={5} style={{ marginBottom: '5px' }}>
              <Text style={{ display: 'flex', alignItems: 'center' }}>
                {'적립일 기준 '}
              </Text>
              <InputNumber
                value={inputValue?.expired_date}
                onChange={(e) => onChange('expired_date', e)}
                placeholder="만료 일수를 입력해 주세요"
                style={{ flex: 0.8 }}
                suffix={'일'}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
              <Text style={{ display: 'flex', alignItems: 'center' }}>
                {'만료'}
              </Text>
            </Flex>
          ) : (
            <Flex style={{ marginBottom: '5px' }}>
              <Text>
                {' '}
                차감 가능 포인트: {calculatePoint('NOT_EXPIRED_POINT')}P
              </Text>
            </Flex>
          )}
          <Flex gap={10} vertical="true">
            <InputNumber
              value={inputValue?.point}
              onChange={(e) => onChange('point', e)}
              placeholder={
                buttonType === 'REWARD' ? '지급 적립금' : '차감 적립금'
              }
              max={
                buttonType === 'DEDUCTED' && calculatePoint('NOT_EXPIRED_POINT')
              }
              suffix={'P'}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              style={{ width: '100%' }}
            />
            <Input
              value={inputValue?.memo}
              onChange={(e) => onChange('memo', e.target.value)}
              placeholder={'사유를 입력 하세요'}
            ></Input>
            <Button
              type="primary"
              danger={buttonType === 'REWARD' ? false : true}
              onClick={() => {
                onReservePoint(currentUserData, inputValue, buttonType);
                setTimeout(() => {
                  setIsOpen(false);
                  setInputValue({
                    point: 0,
                    expired_date: 0,
                    memo: null,
                  });
                  setButtonType(null);
                }, 1000);
              }}
            >
              {buttonType === 'REWARD' ? '지급' : '차감'}
            </Button>
          </Flex>
        </Content>
      </ModalLayout>
    </>
  );
};
UserPointHistoryPresenter.defaultProps = {
  users: [],
};
export default UserPointHistoryPresenter;
